import { useState } from 'react';
import { Plus, ArrowUp, ArrowDown, Trash2, Rows3 } from 'lucide-react';

// ─── Shared form-schema builder ──────────────────────────────────────────────
// Extracted from LogbookTemplates.tsx's template form builder (Logbooks Phase 2) so the same
// add/remove/reorder/edit-type UI can target a work order's own ad-hoc custom_form_schema
// instead of a shared logbook_templates.form_schema — one builder implementation, two callers.

export type FormFieldType = 'text' | 'number' | 'date' | 'textarea' | 'select' | 'checkbox';

export interface FormField {
  key: string;
  label: string;
  // 'section_break' is a builder-only pseudo-field (never a real renderable input) — a divider
  // marker inserted via "Add section break" that groups every subsequent field under it into one
  // section, until the next marker. Stripped out at save time (see splitFieldsIntoSections);
  // never appears in a saved LogbookFormSchema.fixed_fields. Deliberately NOT in FIELD_TYPES so
  // the type dropdown can never let a user turn a real field into one by hand.
  type: FormFieldType | 'section_break';
  options?: string[];
  required?: boolean;
  // Optional range limits for number-type fields — admin/org_admin authored (see
  // FormSchemaBuilder's showRangeLimits prop), checked server-side on submission so an
  // out-of-range reading can optionally auto-create a follow-up work order. Meaningless for any
  // other field type.
  min?: number;
  max?: number;
  // Set on real fields (never on a section_break marker itself) once splitFieldsIntoSections has
  // run — carries the section this field belongs to through to the saved LogbookFormSchema.
  section_id?: string;
}

export const FIELD_TYPES: { value: FormFieldType; label: string }[] = [
  { value: 'text', label: 'Text' }, { value: 'number', label: 'Number' }, { value: 'date', label: 'Date' },
  { value: 'textarea', label: 'Long text' }, { value: 'select', label: 'Dropdown' }, { value: 'checkbox', label: 'Checkbox' },
];

// A fully-controlled input whose value is `field.options.join(', ')` re-derives that value on
// every keystroke — split(',').map(trim).filter(Boolean) drops the empty trailing segment right
// after a comma, so typing "Rm 101," immediately snapped the field back to "Rm 101" (comma
// silently eaten) before the user could type the next option. Local text state fixes this: the
// input keeps whatever the user is typing verbatim, and only commits the parsed array on blur.
// Keyed by the field's own key (not array index) so reordering fields doesn't smear one field's
// in-progress typing onto another's DOM node.
function SelectOptionsInput({ initialOptions, onCommit }: { initialOptions: string[]; onCommit: (opts: string[]) => void }) {
  const [text, setText] = useState(initialOptions.join(', '));
  return (
    <input
      className="form-input" placeholder="Options, comma-separated"
      value={text}
      onChange={e => setText(e.target.value)}
      onBlur={() => onCommit(text.split(',').map(o => o.trim()).filter(Boolean))}
      style={{ fontSize: 12, padding: '5px 9px' }}
    />
  );
}

export function slugifyKey(label: string, existing: string[]): string {
  const base = label.toLowerCase().trim().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'field';
  if (!existing.includes(base)) return base;
  let i = 2;
  while (existing.includes(`${base}_${i}`)) i++;
  return `${base}_${i}`;
}

// ─── Section-break marker <-> LogbookFormSchema.sections/section_id conversion ───────────────
// The builder's flat `fields` array (what FormSchemaBuilder itself edits/reorders) can contain
// 'section_break' marker entries; the saved schema shape never does — it has a separate
// `sections: {section_id,label}[]` list plus a `section_id` tag on each real field (identical
// shape to LogbookGridRow's own section_id, see LogbookFormRenderer.tsx). These two functions
// convert between the two representations at save/load time so only the builder needs to know
// markers exist at all.

// Return type narrows `type` back down to FormFieldType (excluding 'section_break') -- every
// section_break entry in the input is consumed into `sections` above and never reaches
// `outFields`, but TS can't infer that from the loop, so it's spelled out explicitly here. This
// keeps the result structurally identical to LogbookFormField[] for callers that save it into a
// LogbookFormSchema.fixed_fields.
export function splitFieldsIntoSections(fields: FormField[]): { fields: Array<Omit<FormField, 'type'> & { type: FormFieldType }>; sections: { section_id: string; label: string }[] } {
  const sections: { section_id: string; label: string }[] = [];
  const outFields: Array<Omit<FormField, 'type'> & { type: FormFieldType }> = [];
  let currentSectionId: string | undefined;
  for (const f of fields) {
    if (f.type === 'section_break') {
      const label = f.label.trim() || 'Section';
      const sectionId = slugifyKey(label, sections.map(s => s.section_id));
      sections.push({ section_id: sectionId, label });
      currentSectionId = sectionId;
      continue;
    }
    outFields.push({ ...f, type: f.type as FormFieldType, section_id: currentSectionId });
  }
  return { fields: outFields, sections };
}

export function mergeSectionsIntoFields(fields: FormField[], sections: { section_id: string; label: string }[]): FormField[] {
  if (sections.length === 0) return fields;
  const labelBySection = new Map(sections.map(s => [s.section_id, s.label]));
  const result: FormField[] = [];
  let lastSectionId: string | undefined;
  for (const f of fields) {
    if (f.section_id !== lastSectionId) {
      if (f.section_id) {
        result.push({ key: `__section_${f.section_id}`, label: labelBySection.get(f.section_id) ?? 'Section', type: 'section_break' });
      }
      lastSectionId = f.section_id;
    }
    result.push(f);
  }
  return result;
}

interface FormSchemaBuilderProps {
  fields: FormField[];
  onChange: (fields: FormField[]) => void;
  // Keys that already existed before this editing session started — used the same way
  // TemplateFormModal used `initial?.form_schema` to stop auto-slugifying a key once a field is
  // no longer brand new (so editing an existing field's label doesn't silently break a key that
  // filled values may already reference).
  stableKeys?: string[];
  emptyLabel?: string;
  addLabel?: string;
  label?: string;
  // Range limits (min/max on number fields) are a Logbook Templates-only feature — admin/org_admin
  // authors them on the template, the same role gate as template CRUD itself. Not surfaced for
  // the ad-hoc WO-local checklist builder (Tasks.tsx), which any manager can use.
  showRangeLimits?: boolean;
  // Section breaks are a Logbook Templates-only feature (same reasoning as showRangeLimits) — the
  // ad-hoc WO-local checklist is always small/flat, so grouping doesn't apply there.
  allowSections?: boolean;
}

export default function FormSchemaBuilder({
  fields, onChange, stableKeys = [], emptyLabel, addLabel, label, showRangeLimits, allowSections,
}: FormSchemaBuilderProps) {
  function addField() {
    onChange([...fields, { key: slugifyKey(`field ${fields.length + 1}`, fields.map(f => f.key)), label: '', type: 'text', required: false }]);
  }
  function addSectionBreak() {
    onChange([...fields, { key: slugifyKey(`section ${fields.filter(f => f.type === 'section_break').length + 1}`, fields.map(f => f.key)), label: '', type: 'section_break' }]);
  }
  function removeField(i: number) {
    onChange(fields.filter((_, idx) => idx !== i));
  }
  function moveField(i: number, dir: -1 | 1) {
    const j = i + dir;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  }
  function updateField(i: number, patch: Partial<FormField>) {
    onChange(fields.map((f, idx) => {
      if (idx !== i) return f;
      const updated = { ...f, ...patch };
      if (patch.label !== undefined && !stableKeys.includes(f.key)) {
        updated.key = slugifyKey(patch.label, fields.filter((_, idx2) => idx2 !== i).map(ff => ff.key));
      }
      return updated;
    }));
  }

  // A field's section is otherwise only implicit — inferable from its position relative to a
  // section_break marker, with no indicator on the field itself. fieldSectionKeys makes that
  // explicit per-index (keyed by the section_break's own `key`, not the saved schema's
  // `section_id` — that's only assigned later by splitFieldsIntoSections at save time) so each
  // field card can show which section it's currently under.
  const fieldSectionKeys: (string | null)[] = [];
  {
    let current: string | null = null;
    for (const f of fields) {
      if (f.type === 'section_break') current = f.key;
      fieldSectionKeys.push(current);
    }
  }
  const sectionMarkers = fields
    .filter((f) => f.type === 'section_break')
    .map((f) => ({ key: f.key, label: f.label.trim() || 'Section' }));

  // Reassigns a field to a different section directly from the dropdown, without requiring the
  // user to drag/reorder it relative to that section's header card. Section membership is purely
  // positional in this array (see splitFieldsIntoSections), so "reassign" means relocating the
  // field: to just before the first section_break for "No section", or to the end of the target
  // section's block (right before the next section_break, or the array end) otherwise.
  function moveFieldToSection(i: number, targetSectionKey: string | null) {
    const field = fields[i];
    const rest = fields.filter((_, idx) => idx !== i);
    let insertAt: number;
    if (targetSectionKey === null) {
      insertAt = rest.findIndex((f) => f.type === 'section_break');
      if (insertAt === -1) insertAt = rest.length;
    } else {
      const markerIdx = rest.findIndex((f) => f.type === 'section_break' && f.key === targetSectionKey);
      if (markerIdx === -1) return;
      insertAt = markerIdx + 1;
      while (insertAt < rest.length && rest[insertAt].type !== 'section_break') insertAt++;
    }
    onChange([...rest.slice(0, insertAt), field, ...rest.slice(insertAt)]);
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <label style={{ fontSize: 12, color: 'var(--text-muted)', display: 'block' }}>{label ?? 'Fields'}</label>
        <div style={{ display: 'flex', gap: 6 }}>
          {allowSections && (
            <button type="button" className="btn btn-ghost btn-sm" onClick={addSectionBreak}>
              <Rows3 size={12} /> Add section break
            </button>
          )}
          <button type="button" className="btn btn-ghost btn-sm" onClick={addField}>
            <Plus size={12} /> {addLabel ?? 'Add field'}
          </button>
        </div>
      </div>
      {fields.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--text-muted)', fontStyle: 'italic', padding: '6px 0' }}>
          {emptyLabel ?? 'No fields added yet.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {fields.map((f, i) => (
            <div key={i} style={f.type === 'section_break'
              ? { display: 'flex', alignItems: 'center', gap: 6, padding: '6px 10px', border: '1px dashed rgba(59,130,246,0.4)', borderRadius: 8, background: 'rgba(59,130,246,0.06)' }
              : { display: 'flex', alignItems: 'flex-start', gap: 6, padding: 10, border: '1px solid var(--border-subtle)', borderRadius: 8, background: 'rgba(255,255,255,0.02)' }
            }>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingTop: f.type === 'section_break' ? 0 : 4 }}>
                <button type="button" disabled={i === 0} onClick={() => moveField(i, -1)} style={{ background: 'none', border: 'none', cursor: i === 0 ? 'default' : 'pointer', color: i === 0 ? 'var(--text-muted)' : 'var(--text-secondary)', opacity: i === 0 ? 0.4 : 1, padding: 0 }}><ArrowUp size={13} /></button>
                <button type="button" disabled={i === fields.length - 1} onClick={() => moveField(i, 1)} style={{ background: 'none', border: 'none', cursor: i === fields.length - 1 ? 'default' : 'pointer', color: i === fields.length - 1 ? 'var(--text-muted)' : 'var(--text-secondary)', opacity: i === fields.length - 1 ? 0.4 : 1, padding: 0 }}><ArrowDown size={13} /></button>
              </div>
              {f.type === 'section_break' ? (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Rows3 size={13} style={{ color: '#60a5fa', flexShrink: 0 }} />
                  <input
                    className="form-input" placeholder="Section name (e.g. West Long Hallway)" value={f.label}
                    onChange={e => updateField(i, { label: e.target.value })}
                    style={{ flex: 1, fontSize: 13, padding: '6px 10px' }}
                  />
                </div>
              ) : (
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input
                      className="form-input" placeholder="Field label" value={f.label}
                      onChange={e => updateField(i, { label: e.target.value })}
                      style={{ flex: 1, fontSize: 13, padding: '6px 10px' }}
                    />
                    <select
                      value={f.type}
                      onChange={e => updateField(i, { type: e.target.value as FormFieldType })}
                      style={{ fontSize: 13, padding: '6px 10px', borderRadius: 6, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', color: 'var(--text-primary)' }}
                    >
                      {FIELD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </div>
                  {allowSections && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Section:</span>
                      <select
                        value={fieldSectionKeys[i] ?? ''}
                        onChange={e => moveFieldToSection(i, e.target.value || null)}
                        style={{
                          fontSize: 12, padding: '4px 8px', borderRadius: 6,
                          background: 'var(--bg-elevated)', border: '1px solid var(--border-default)',
                          color: fieldSectionKeys[i] ? 'var(--text-primary)' : 'var(--text-muted)',
                        }}
                      >
                        <option value="">No section</option>
                        {sectionMarkers.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                      </select>
                    </div>
                  )}
                  {f.type === 'select' && (
                    <SelectOptionsInput
                      key={f.key}
                      initialOptions={f.options ?? []}
                      onCommit={opts => updateField(i, { options: opts })}
                    />
                  )}
                  {f.type === 'number' && showRangeLimits && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Range limits (optional):</span>
                      <input
                        type="number" placeholder="Min" value={f.min ?? ''}
                        onChange={e => updateField(i, { min: e.target.value === '' ? undefined : Number(e.target.value) })}
                        style={{ width: 70, fontSize: 12, padding: '5px 8px', borderRadius: 6, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', color: 'var(--text-primary)' }}
                      />
                      <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>–</span>
                      <input
                        type="number" placeholder="Max" value={f.max ?? ''}
                        onChange={e => updateField(i, { max: e.target.value === '' ? undefined : Number(e.target.value) })}
                        style={{ width: 70, fontSize: 12, padding: '5px 8px', borderRadius: 6, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', color: 'var(--text-primary)' }}
                      />
                    </div>
                  )}
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: 'var(--text-muted)', cursor: 'pointer' }}>
                    <input type="checkbox" checked={f.required ?? false} onChange={e => updateField(i, { required: e.target.checked })} />
                    Required
                  </label>
                </div>
              )}
              <button type="button" onClick={() => removeField(i)} title={f.type === 'section_break' ? 'Remove section break' : 'Remove field'} style={{ background: 'none', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 6, padding: '4px 7px', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center' }}>
                <Trash2 size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
